import { Link, useLocation } from "react-router-dom";
import { Linkedin } from "lucide-react";
import { XIcon, InstagramIcon, WhatsAppIcon, FacebookIcon, LinkedInIcon } from "@/components/ui/SocialIcons";

export function AboutFooter() {
  const location = useLocation();
  const isLandingPage = location.pathname === '/';

  return (
    <footer className="bg-card text-foreground">
      <div className="container max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <p className="font-bold text-xl mb-4 text-foreground">Metefy</p>
            <p className="text-foreground/80 mb-6">
              Smart QR identity system for lost item recovery. Protect your belongings with digital identity.
            </p>
            <div className="flex items-center gap-3">
              <a href="https://instagram.com/mete_fy" target="_blank" rel="noopener noreferrer"
                 aria-label="Follow us on Instagram"
                 className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white p-1.5 rounded-full shadow transition-transform duration-300 hover:scale-110">
                <InstagramIcon className="h-4 w-4" />
              </a>
              <a href="https://x.com/mete_fy" target="_blank" rel="noopener noreferrer"
                 aria-label="Follow us on X (Twitter)"
                 className="bg-black hover:bg-gray-800 text-white p-1.5 rounded-full shadow transition-transform duration-300 hover:scale-110">
                <XIcon className="h-4 w-4" />
              </a>
              <a href="https://wa.me/918307562996?text=Hello%20!%20I'm%20interested%20in%20Metefy%20QR%20stickers." target="_blank" rel="noopener noreferrer"
                 aria-label="Contact us on WhatsApp"
                 className="bg-green-500 hover:bg-green-600 text-white p-1.5 rounded-full shadow transition-transform duration-300 hover:scale-110">
                <WhatsAppIcon className="h-4 w-4" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61575578170519" target="_blank" rel="noopener noreferrer"
                 aria-label="Follow us on Facebook"
                 className="bg-blue-600 hover:bg-blue-700 text-white p-1.5 rounded-full shadow transition-transform duration-300 hover:scale-110">
                <FacebookIcon className="h-4 w-4" />
              </a>
              <a href="https://www.pinterest.com/mete_fy" target="_blank" rel="noopener noreferrer"
                 aria-label="Follow us on Pinterest"
                 className="bg-red-600 hover:bg-red-700 text-white p-1.5 rounded-full shadow transition-transform duration-300 hover:scale-110">
                {/* simple pin glyph */}
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 0C5.395 0 .08 5.315.08 11.96c0 4.96 3.091 9.188 7.456 10.905-.103-.927-.195-2.346.04-3.358.213-.91 1.37-5.79 1.37-5.79s-.35-.71-.35-1.757c0-1.646.954-2.876 2.143-2.876 1.01 0 1.497.758 1.497 1.666 0 1.015-.647 2.531-.982 3.937-.279 1.167.592 2.118 1.755 2.118 2.106 0 3.721-2.22 3.721-5.427 0-2.835-2.036-4.822-4.942-4.822-3.367 0-5.345 2.523-5.345 5.135 0 1.018.393 2.111.885 2.704.097.117.111.22.084.339-.093.373-.304 1.166-.345 1.328-.053.213-.173.259-.4.157-1.493-.695-2.426-2.879-2.426-4.64 0-3.77 2.74-7.234 7.9-7.234 4.145 0 7.364 2.951 7.364 6.896 0 4.117-2.594 7.432-6.196 7.432-1.21 0-2.346-.628-2.734-1.37l-.744 2.836c-.27 1.04-1.003 2.342-1.495 3.138 1.123.348 2.314.536 3.552.536 6.646 0 11.96-5.314 11.96-11.96C23.92 5.315 18.686 0 12.04 0z"/></svg>
              </a>
            </div>
          </div>

          <div>
            <p className="font-bold text-xl mb-4 text-foreground">Quick Links</p>
            <ul className="space-y-2">
              <li>
                {isLandingPage ? (
                  <button type="button" onClick={() => document.getElementById('hero')?.scrollIntoView({ behavior: 'smooth' })} className="text-foreground/80 hover:text-primary transition-colors w-full text-left">Home</button>
                ) : (
                  <Link to="/#hero" className="text-foreground/80 hover:text-primary transition-colors">Home</Link>
                )}
              </li>
              <li>
                {isLandingPage ? (
                  <button type="button" onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="text-foreground/80 hover:text-primary transition-colors w-full text-left">Features</button>
                ) : (
                  <Link to="/#features" className="text-foreground/80 hover:text-primary transition-colors">Features</Link>
                )}
              </li>
              <li>
                {isLandingPage ? (
                  <button type="button" onClick={() => document.getElementById('find-owner')?.scrollIntoView({ behavior: 'smooth' })} className="text-foreground/80 hover:text-primary transition-colors w-full text-left">Find Owner</button>
                ) : (
                  <Link to="/#find-owner" className="text-foreground/80 hover:text-primary transition-colors">Find Owner</Link>
                )}
              </li>
              <li>
                {isLandingPage ? (
                  <button type="button" onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="text-foreground/80 hover:text-primary transition-colors w-full text-left">FAQ</button>
                ) : (
                  <Link to="/#faq" className="text-foreground/80 hover:text-primary transition-colors">FAQ</Link>
                )}
              </li>
              <li><Link to="/found-items-map" className="text-foreground/80 hover:text-primary transition-colors">Found Items Map</Link></li>
              <li><Link to="/free-custom-bulk-qr-code-generator" className="text-foreground/80 hover:text-primary transition-colors">Free QR Code Generator</Link></li>
              <li><Link to="/sticker-store" className="text-foreground/80 hover:text-primary transition-colors">Sticker Store</Link></li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-xl mb-4 text-foreground">Other Pages</p>
            <ul className="space-y-2">
              <li><Link to="/blog" className="text-foreground/80 hover:text-primary transition-colors">Blogs</Link></li>
              <li>
                <Link
                  to="/blog/fix-supabase-down-on-jio-and-jiofiber-by-hiding-supabase-behind-your-own-domain-free-tier"
                  className="text-foreground/80 hover:text-primary transition-colors"
                >
                  Fix Supabase down on Jio/JioFiber
                </Link>
              </li>
              <li><Link to="/flipbook" className="text-foreground/80 hover:text-primary transition-colors">Flipbook</Link></li>
              <li><Link to="/games" className="text-foreground/80 hover:text-primary transition-colors">Games</Link></li>
              <li><Link to="/puzzle" className="text-foreground/80 hover:text-primary transition-colors">Puzzle</Link></li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-xl mb-4 text-foreground">Legal</p>
            <ul className="space-y-2">
              <li><Link to="/privacy-policy" className="text-foreground/80 hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-and-conditions" className="text-foreground/80 hover:text-primary transition-colors">Terms & Conditions</Link></li>
              <li><Link to="/refund-policy" className="text-foreground/80 hover:text-primary transition-colors">Refund Policy</Link></li>
            </ul>
          </div>

          <div>
            <p className="font-bold text-xl mb-4 text-foreground">Contact</p>
            <p className="text-foreground/80 mb-2">
              Have questions? Reach out to us on X.
            </p>
            <a href="https://x.com/mete_fy" target="_blank" rel="noopener noreferrer" 
               className="flex items-center transition-all duration-300"
               aria-label="Contact us on X (Twitter)">
              <span className="bg-black hover:bg-gray-800 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-gray-400/50 mr-2">
                <XIcon className="h-4 w-4" />
              </span>
              <span className="text-primary hover:opacity-80">@mete_fy</span>
            </a>
          </div>

          <div>
            <p className="font-bold text-xl mb-4 text-foreground">Share Metefy</p>
            <div className="flex items-center gap-3">
              <a href="https://twitter.com/intent/tweet?url=https%3A%2F%2Fmetefy.com&text=Check%20out%20Metefy%20-%20The%20smart%20QR%20identity%20system%20for%20lost%20item%20recovery%20%40mete_fy"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="bg-black hover:bg-gray-800 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-gray-400/50"
                 aria-label="Share on X (Twitter)">
                <XIcon className="h-4 w-4" />
              </a>
              <a href="https://www.linkedin.com/shareArticle?mini=true&url=https://metefy.com"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="bg-[#0A66C2] hover:bg-[#09539a] text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-blue-400/50"
                 aria-label="Share on LinkedIn">
                <LinkedInIcon className="h-4 w-4" />
              </a>
              <a href="https://www.facebook.com/sharer/sharer.php?u=https://metefy.com"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-blue-400/50"
                 aria-label="Share on Facebook">
                <FacebookIcon className="h-4 w-4" />
              </a>
              <a href="https://wa.me/?text=Check%20out%20Metefy%20-%20The%20smart%20QR%20identity%20system%20for%20lost%20item%20recovery%20https://metefy.com"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-green-400/50"
                 aria-label="Share on WhatsApp">
                <WhatsAppIcon className="h-4 w-4" />
              </a>
              <a href="https://www.pinterest.com/pin/create/button/?url=https://metefy.com&media=&description=Check%20out%20Metefy"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow transition-all duration-300 scale-110 hover:scale-125 hover:ring-2 hover:ring-red-400/50"
                 aria-label="Share on Pinterest">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 0C5.395 0 .08 5.315.08 11.96c0 4.96 3.091 9.188 7.456 10.905-.103-.927-.195-2.346.04-3.358.213-.91 1.37-5.79 1.37-5.79s-.35-.71-.35-1.757c0-1.646.954-2.876 2.143-2.876 1.01 0 1.497.758 1.497 1.666 0 1.015-.647 2.531-.982 3.937-.279 1.167.592 2.118 1.755 2.118 2.106 0 3.721-2.22 3.721-5.427 0-2.835-2.036-4.822-4.942-4.822-3.367 0-5.345 2.523-5.345 5.135 0 1.018.393 2.111.885 2.704.097.117.111.22.084.339-.093.373-.304 1.166-.345 1.328-.053.213-.173.259-.4.157-1.493-.695-2.426-2.879-2.426-4.64 0-3.77 2.74-7.234 7.9-7.234 4.145 0 7.364 2.951 7.364 6.896 0 4.117-2.594 7.432-6.196 7.432-1.21 0-2.346-.628-2.734-1.37l-.744 2.836c-.27 1.04-1.003 2.342-1.495 3.138 1.123.348 2.314.536 3.552.536 6.646 0 11.96-5.314 11.96-11.96C23.92 5.315 18.686 0 12.04 0z"/></svg>
              </a>
            </div>
          </div>
        </div>
        
        <div className="border-t border-border mt-10 pt-6 flex flex-col sm:flex-row justify-between items-center text-foreground/80 mb-12">
          <p>&copy; {new Date().getFullYear()} Metefy. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
} 